import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { kpiAdminService } from '../../services/kpiAdminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';

export function UserKPIAssignments() {
  const [users, setUsers] = useState([]);
  const [systems, setSystems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  // Per-cell in-flight guard. Key: `${userId}:${systemId}`.
  const [pendingToggles, setPendingToggles] = useState(new Set());

  const loadData = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      const [usersData, systemsData, assignmentsData] = await Promise.all([
        kpiAdminService.getAllUsers(),
        kpiAdminService.getKPISystems(),
        kpiAdminService.getUserAssignments(),
      ]);
      if (isCancelled()) return;
      setUsers(usersData);
      setSystems(systemsData.filter(s => s.is_active));
      setAssignments(assignmentsData);
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(loadData, []);

  const isAssigned = (userId, systemId) => {
    return assignments.some(
      a => a.user_id === userId && a.kpi_system_id === systemId && a.is_active
    );
  };

  const handleToggle = async (userId, systemId) => {
    const key = `${userId}:${systemId}`;
    if (pendingToggles.has(key)) return; // in-flight guard

    const previousAssignments = assignments;
    const wasAssigned = isAssigned(userId, systemId);

    // Optimistic update: add or drop the assignment row immediately.
    if (wasAssigned) {
      setAssignments((prev) =>
        prev.filter((a) => !(a.user_id === userId && a.kpi_system_id === systemId))
      );
    } else {
      setAssignments((prev) => [
        ...prev,
        // Placeholder id so React keys stay unique even before the server
        // returns the real row; isAssigned() only cares about (user_id,
        // kpi_system_id, is_active) so the shape is enough.
        { id: `optimistic-${key}`, user_id: userId, kpi_system_id: systemId, is_active: true },
      ]);
    }
    setPendingToggles((prev) => new Set(prev).add(key));

    try {
      setError(null);
      if (wasAssigned) {
        await kpiAdminService.removeKPISystemFromUser(userId, systemId);
      } else {
        await kpiAdminService.assignKPISystemToUser(userId, systemId);
      }
    } catch (err) {
      setAssignments(previousAssignments);
      toast.error(mapSupabaseError(err));
    } finally {
      setPendingToggles((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="user-kpi-assignments">
      <h2>User KPI Assignments</h2>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="assignments-container">
        <div className="users-list">
          <h3>Users</h3>
          <div className="user-selector">
            {users.map(user => (
              <div
                key={user.id}
                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => setSelectedUser(user)}
              >
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
                <div className="user-role">{user.role}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="assignments-grid">
          <h3>Assignments Matrix</h3>
          {selectedUser ? (
            <div className="assignment-form">
              <h4>{selectedUser.name}'s Systems</h4>
              <div className="system-checkboxes">
                {systems.map(system => {
                  const key = `${selectedUser.id}:${system.id}`;
                  return (
                    <label key={system.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={isAssigned(selectedUser.id, system.id)}
                        onChange={() => handleToggle(selectedUser.id, system.id)}
                        disabled={pendingToggles.has(key)}
                      />
                      <span>{system.name}</span>
                      <span className="system-country">({system.country})</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <p>Select a user to manage their system assignments</p>
          )}
        </div>
      </div>
    </div>
  );
}
